import React from 'react';
import { useRouter } from 'next/compat/router';

import useUI from 'hooks/use-ui';
import useDispensary from 'src/dispensary/hooks/use-dispensary';

import PageTitle from './title';
import PageDescription from './description';
import Favicons from './favicons';
import ProductTags from './product-tags';
import DispensaryTags from './dispensary-tags';
import CategoryTags from './category-tags';
import Meta from './meta';
import CanonicalTag from './canonical-tag';
import AppleTouchIcons from './apple-touch-icons';

import { getPageVariant, isDutchieMainProductDetailPage } from './utils.ts';

const CATEGORY_JSON_LD_VARIANTS = [
  'dispensaryProductCategory',
  'dispensaryProductSubcategory',
  'dispensaryAllProducts',
];

function useMetaRobotsContent(customRobotsRule, query, asPath, hideFromSearchEngines) {
  const UI = useUI();

  // Super-admin-only setting (Dispensary Admin -> Settings -> Store Menu -> Internal). Takes
  // precedence over everything else so sandbox/test stores never get indexed, regardless of page
  // variant or any other robots override.
  if (hideFromSearchEngines) {
    return `noindex,nofollow`;
  }

  if (customRobotsRule) {
    return customRobotsRule;
  }

  if (UI.isVariant(`embedded`)) {
    return `noindex,indexifembedded`;
  }

  if (UI.isVariant(`kiosk`) || isDutchieMainProductDetailPage(query, asPath)) {
    return `noindex,follow`;
  }

  return `index,follow`;
}

export default function MetaTags(props) {
  const { query, asPath, isReady } = useRouter() ?? {};
  const UI = useUI();
  // Cheap: only fires a network request when the current route has a `cName`; otherwise skipped.
  // On dispensary/store-front routes this shares the Apollo cache with the query the page's own
  // dispensary layout already issues, so this doesn't add an extra round trip in practice.
  const { dispensary } = useDispensary();
  const hideFromSearchEngines = Boolean(dispensary?.storeSettings?.hideFromSearchEngines);
  const { variant = getPageVariant(query, asPath), location, customRobotsRule = undefined } = props;
  const robotsContent = useMetaRobotsContent(customRobotsRule, query, asPath, hideFromSearchEngines);
  const isEmbeddedOrStoreFront = UI.isVariant(`store-front`) || UI.isVariant(`embedded`);

  if (!isReady) {
    return null;
  }

  return (
    <>
      <PageTitle variant={variant} location={location} isEmbeddedOrStoreFront={isEmbeddedOrStoreFront} />
      <PageDescription variant={variant} location={location} isEmbeddedOrStoreFront={isEmbeddedOrStoreFront} />
      <Favicons variant={variant} isKioskOrStoreFront={UI.isVariant(`kiosk`) || UI.isVariant(`store-front`)} />
      <Meta property='viewport' content='width=device-width, initial-scale=1' />
      <Meta property='robots' content={robotsContent} />
      <Meta property='googlebot' content={robotsContent} />
      <CanonicalTag />
      <AppleTouchIcons />
      {(variant === 'product' || variant === 'dispensaryProduct') && <ProductTags />}
      {variant === 'dispensary' && <DispensaryTags />}
      {CATEGORY_JSON_LD_VARIANTS.includes(variant) && <CategoryTags />}
    </>
  );
}
